// features/projects/templates/professional/resources.js
// ═══════════════════════════════════════════════════════════
// PROFESSIONAL RESOURCES — sources + (later) files
//
// Per spec §V: two-section layout — public sources toggle (live)
// + per-project file upload (Session 6.5 placeholder).
//
// Public sources: lifts SourcesTab from features/lab-feature.js.
// Same component renders inside the Lab feature page and here.
// File upload + per-user API keys arrive in subsequent sessions.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { L, FONT } from './tokens';
import { SourcesTab } from '../../../lab-feature';

export default function ProfessionalResources({ workspaceId, projectId, supabase }) {
  // Pull access token for the Lab API endpoints. Refresh on mount.
  const [accessToken, setAccessToken] = useState(null);
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!cancelled) setAccessToken(data?.session?.access_token || null);
      } catch {
        if (!cancelled) setAccessToken(null);
      }
    })();
    return () => { cancelled = true; };
  }, [supabase]);

  // Lab API endpoints query by project_id; fall back to workspaceId
  // for legacy projects without the migrated project_id link.
  const project = { id: projectId || workspaceId };

  return (
    <div style={{
      height: '100%', background: L.bg, color: L.text,
      fontFamily: FONT.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header />
      <div style={{
        flex: 1, minHeight: 0, overflow: 'auto',
        padding: '20px 24px',
      }}>
        {/* Section 1: public sources (live, lifted from Lab) */}
        <Section title="Public Sources" subtitle="Toggle the data sources auto-wired into this project">
          <SourcesTab project={project} accessToken={accessToken} />
        </Section>

        {/* Section 2: my resources (placeholder for Session 6.5) */}
        <Section title="My Resources" subtitle="Upload your own data — coming in a later session">
          <PlaceholderPanel
            title="File upload arrives in a later session"
            body="This section will accept CSV / JSON / Excel uploads scoped to this project. Uploaded files will live alongside public sources and feed the same widget data layer."
          />
        </Section>

        {/* Section 3: API keys (placeholder for Session 8) */}
        <Section title="API Keys" subtitle="Per-user keys for paid data sources — coming in a later session">
          <PlaceholderPanel
            title="API key management arrives in a later session"
            body="Configure your own keys for sources that require them (e.g. premium data feeds). Keys stay scoped to your account, never leak across projects."
          />
        </Section>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${L.b1}`, background: L.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(107,143,168,.10)', border: `1px solid rgba(107,143,168,.25)`,
        color: L.steel,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: L.steel }} />
        Resources — Sources & Files
      </div>
      <div style={{ fontSize: 9, color: L.dim }}>
        Public sources toggleable per project · upload your own data · manage API keys
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        marginBottom: 14, paddingBottom: 8,
        borderBottom: `1px solid ${L.b1}`,
      }}>
        <div style={{
          fontFamily: FONT.serif, fontSize: 20, fontWeight: 300,
          color: L.text, marginBottom: 4,
        }}>
          {title}
        </div>
        <div style={{
          fontFamily: FONT.sans, fontSize: 11, color: L.dim,
          fontStyle: 'italic',
        }}>
          {subtitle}
        </div>
      </div>
      {children}
    </div>
  );
}

function PlaceholderPanel({ title, body }) {
  return (
    <div style={{
      padding: '18px 22px',
      background: L.s2, border: `1px dashed ${L.b2}`, borderRadius: 6,
    }}>
      <div style={{
        fontFamily: FONT.sans, fontSize: 13, color: L.text,
        fontWeight: 500, marginBottom: 8,
      }}>
        {title}
      </div>
      <div style={{
        fontFamily: FONT.sans, fontSize: 12, color: L.mid,
        lineHeight: 1.6,
      }}>
        {body}
      </div>
    </div>
  );
}
